﻿/// <reference path="../../../../../typings/index.d.ts" />
/// <reference path="../controls.module.ts" />

module Meevo.Components.Controls {
    'use strict';
    import WeekdayNameEnum = Meevo.Model.WeekdayNameEnum;

    export enum PresetStatus {
        Custom = 0,
        Today,
        Yesterday,
        ThisWeek,
        LastWeek,
        LastSevenDays,
        PreviousTwoWeeks,
        ThisMonth,
        LastMonth,
        ThisQuarter,
        LastQuarter,
        NextQuarter,
        ThisYear,
        LastYear,
        Tomorrow,
        NextWeek,
        NextMonth,
        NextYear,
        LastTwelveMonths,
        LastThirteenMonths,
        LastTwentyFourHours,
        LastFourteenDays,
        LastThirtyDays,
        LastSixtyDays,
        LastNinetyDays,
        LastOneTwentyDays,
        LastOneEightyDays,
        SpecificYear,
        LastTwelveMonthsIncludingToday,
        YearToDate
    }

    export interface IFromToStructure {
        from: Date;
        to: Date;
    }

    export interface IPresetOptions {
        minDate?: Date;
        maxDate?: Date;
        moment?: any;
        ifForReport?: Boolean;
        isLocaleAware?: Boolean;
        startDayOfWeek?: number;
        timeZoneOffset?: string;
    }


    const parseQuarter = (prev: boolean = false): IFromToStructure => {
        let quarter: number = moment().quarter(),
            from, to;

        if (prev) {
            quarter -= 1;
        }

        if (quarter === 0) { // prev quarter === Q4 prev year
            let prevYear = moment().year() - 1;

            from = moment().month(9).year(prevYear).startOf('month').toDate();
            to = moment().month(11).year(prevYear).endOf('month').toDate();
        } else {
            from = moment().month(quarter * 3 - 3).startOf('month').toDate();
            to = moment().month(quarter * 3 - 1).endOf('month').toDate();
        }

        return {
            from,
            to
        }
    };

    const changeDateForReportOnly = (scope: IFromToStructure, option: IPresetOptions) => {
        if (option.minDate instanceof Date && moment(option.minDate).isAfter(scope.from, 'day') && option.ifForReport) {
            scope.from = option.minDate;
        }

        if (option.maxDate instanceof Date && moment(option.maxDate).isBefore(scope.to, 'day') && option.ifForReport) {
            scope.to = option.maxDate;
        }
    };

    export abstract class Preset {

        static presetStatus: PresetStatus;

        get moment() {
            let businessMoment = moment();
            let locale = { ...businessMoment['_locale'] };
            locale['_week'] = { ...locale['_week'] };   
            businessMoment['_locale'] = locale;
    
            if (this.options.ifForReport) {
                if (this.options.timeZoneOffset && this.options.timeZoneOffset !== '') {
                    let localUtcOffset = moment().utcOffset();
                    let businessUtcOffset = moment().utcOffset(this.options.timeZoneOffset).utcOffset();
                    let businessOffsetFromLocal = businessUtcOffset - localUtcOffset;
                    businessMoment = moment().add('minutes', businessOffsetFromLocal);
                }

                if (this.options.startDayOfWeek !== undefined) {
                    locale['_week']['dow'] = DaysOfWeek[Meevo.Model.WeekdayNameEnum[this.options.startDayOfWeek]];
                }
            }
            else if (this.options.isLocaleAware) {
                locale['_week']['dow'] = this.options.startDayOfWeek;
            }

            return businessMoment;
        }

        constructor(protected options: IPresetOptions = <IPresetOptions>{}) {
        }

        validate(presetValidationRules: IValidationRuleObj[]): boolean {
            let currentDateRange: IFromToStructure = <IFromToStructure>{};
            this.getDate(currentDateRange);

            return presetValidationRules.filter(validationRule => {
                return validationRule.rule(currentDateRange.from, currentDateRange.to, ...validationRule.data);
            }).length === presetValidationRules.length;
        }

        abstract getDate<T extends IFromToStructure>(obj: T, option?: IPresetOptions): void;
    }

    export class CustomPreset extends Preset {

        static presetStatus = PresetStatus.Custom;

        getDate() {
        }

        constructor(protected options: IPresetOptions) {
            super();
        }
    }

    export class TodayPreset extends Preset {

        static presetStatus = PresetStatus.Today;

        getDate(scope: IFromToStructure, option: IPresetOptions = this.options) {
            scope.from = this.moment.startOf('day').toDate();
            scope.to = this.moment.endOf('day').toDate();
        }

        constructor(protected options: IPresetOptions) {
            super();
        }
    }

    export class YesterdayPreset extends Preset {

        static presetStatus = PresetStatus.Yesterday;

        getDate(scope: IFromToStructure, option: IPresetOptions = this.options) {
            scope.from = this.moment.add(-1, 'days').startOf('day').toDate();
            scope.to = this.moment.add(-1, 'days').endOf('day').toDate();
        }

        constructor(protected options: IPresetOptions) {
            super();
        }
    }

    export class ThisWeekPreset extends Preset {

        static presetStatus = PresetStatus.ThisWeek;

        getDate(scope: IFromToStructure, option: IPresetOptions = this.options) {
            scope.from = this.moment.startOf('week').toDate();
            scope.to = this.moment.endOf('week').toDate();

            changeDateForReportOnly(scope, option);
        }

        constructor(protected options: IPresetOptions) {
            super();
        }
    }

    export class LastWeekPreset extends Preset {

        static presetStatus = PresetStatus.LastWeek;

        getDate(scope: IFromToStructure, option: IPresetOptions = this.options) {
            scope.from = this.moment.subtract(1, 'weeks').startOf('week').toDate();
            scope.to = this.moment.subtract(1, 'weeks').endOf('week').toDate();
        }

        constructor(protected options: IPresetOptions) {
            super();
        }
    }

    export class LastSevenDaysPreset extends Preset {

        static presetStatus = PresetStatus.LastSevenDays;

        getDate(scope: IFromToStructure, option: IPresetOptions = this.options) {
            scope.from = this.moment.subtract(6, 'days').startOf('day').toDate();
            scope.to = this.moment.endOf('day').toDate();
        }

        constructor(protected options: IPresetOptions) {
            super();
        }
    }

    export class LastTwelveMonthsPreset extends Preset {

        static presetStatus = PresetStatus.LastTwelveMonths;

        getDate(scope: IFromToStructure, option: IPresetOptions = this.options) {
            scope.from = this.moment.subtract(12, 'months').startOf('month').toDate();
            scope.to = this.moment.subtract(1, 'months').endOf('month').toDate();
        }

        constructor(protected options: IPresetOptions) {
            super();
        }
    }

    export class LastTwelveMonthsIncludingTodayPreset extends Preset {

        static presetStatus = PresetStatus.LastTwelveMonthsIncludingToday;

        getDate(scope: IFromToStructure, option: IPresetOptions = this.options) {
            scope.from = this.moment.subtract(1, 'years').toDate();
            scope.to = this.moment.toDate();
        }
    }

    export class YearToDatePreset extends Preset {

        static presetStatus = PresetStatus.YearToDate;

        getDate(scope: IFromToStructure, option: IPresetOptions = this.options) {
            scope.from = this.moment.startOf('years').toDate();
            scope.to = this.moment.endOf('day').toDate();
        }

    }

    export class LastThirteenMonthsPreset extends Preset {

        static presetStatus = PresetStatus.LastThirteenMonths;

        getDate(scope: IFromToStructure, option: IPresetOptions = this.options) {
            scope.from = this.moment.subtract(12, 'months').startOf('month').toDate();
            scope.to = this.moment.endOf('month').toDate();
        }

        constructor(protected options: IPresetOptions) {
            super();
        }
    }

    export class PreviousTwoWeeksPreset extends Preset {

        static presetStatus = PresetStatus.PreviousTwoWeeks;

        getDate(scope: IFromToStructure, option: IPresetOptions = this.options) {
            scope.from = this.moment.subtract(2, 'weeks').startOf('week').toDate();
            scope.to = this.moment.subtract(1, 'weeks').endOf('week').toDate();
        }

        constructor(protected options: IPresetOptions) {
            super();
        }
    }

    export class ThisMonthPreset extends Preset {

        static presetStatus = PresetStatus.ThisMonth;

        getDate(scope: IFromToStructure, option: IPresetOptions = this.options) {
            scope.from = this.moment.startOf('month').toDate();
            scope.to = this.moment.endOf('month').toDate();

            changeDateForReportOnly(scope, option);
        }

        constructor(protected options: IPresetOptions) {
            super();
        }
    }

    export class LastMonthPreset extends Preset {

        static presetStatus = PresetStatus.LastMonth;

        getDate(scope: IFromToStructure, option: IPresetOptions = this.options) {
            scope.from = this.moment.subtract(1, 'months').startOf('month').toDate();
            scope.to = this.moment.subtract(1, 'months').endOf('month').toDate();
        }

        constructor(protected options: IPresetOptions) {
            super();
        }
    }

    export class ThisQuarterPreset extends Preset {

        static presetStatus = PresetStatus.ThisQuarter;

        getDate(scope: IFromToStructure, option: IPresetOptions = this.options) {
            let range = parseQuarter();

            scope.from = range.from;
            scope.to = range.to;

            changeDateForReportOnly(scope, option);
        }

        constructor(protected options: IPresetOptions) {
            super();
        }
    }

    export class LastQuarterPreset extends Preset {

        static presetStatus = PresetStatus.LastQuarter;

        getDate(scope: IFromToStructure, option: IPresetOptions = this.options) {
            let range = parseQuarter(true);

            scope.from = range.from;
            scope.to = range.to;
        }

        constructor(protected options: IPresetOptions) {
            super();
        }
    }

    export class NextQuarterPreset extends Preset {

        static presetStatus = PresetStatus.NextQuarter;

        getDate(scope: IFromToStructure, option: IPresetOptions = this.options) {
            scope.from = this.moment.quarter(this.moment.quarter() + 1).startOf('quarter').toDate();
            scope.to = this.moment.quarter(this.moment.quarter() + 1).endOf('quarter').toDate();
        }

        constructor(protected options: IPresetOptions) {
            super();
        }
    }

    export class ThisYearPreset extends Preset {

        static presetStatus = PresetStatus.ThisYear;

        getDate(scope: IFromToStructure, option: IPresetOptions = this.options) {
            scope.from = this.moment.startOf('years').toDate();
            scope.to = this.moment.endOf('years').toDate();

            changeDateForReportOnly(scope, option);
        }

        constructor(protected options: IPresetOptions) {
            super();
        }
    }

    export class LastYearPreset extends Preset {

        static presetStatus = PresetStatus.LastYear;

        getDate(scope: IFromToStructure, option: IPresetOptions = this.options) {
            scope.from = this.moment.subtract(1, 'years').startOf('years').toDate();
            scope.to = this.moment.subtract(1, 'years').endOf('years').toDate();
        }

        constructor(protected options: IPresetOptions) {
            super();
        }
    }

    export class TomorrowPreset extends Preset {

        static presetStatus = PresetStatus.Tomorrow;

        getDate(scope: IFromToStructure, option: IPresetOptions = this.options) {
            scope.from = this.moment.add(1, 'days').startOf('days').toDate();
            scope.to = this.moment.add(1, 'days').endOf('days').toDate();
        }

        constructor(protected options: IPresetOptions) {
            super();
        }
    }

    export class NextWeekPreset extends Preset {

        static presetStatus = PresetStatus.NextWeek;

        getDate(scope: IFromToStructure, option: IPresetOptions = this.options) {
            scope.from = this.moment.add(1, 'weeks').startOf('weeks').toDate();
            scope.to = this.moment.add(1, 'weeks').endOf('weeks').toDate();
        }

        constructor(protected options: IPresetOptions) {
            super();
        }
    }

    export class NextMonthPreset extends Preset {

        static presetStatus = PresetStatus.NextMonth;

        getDate(scope: IFromToStructure, option: IPresetOptions = this.options) {
            scope.from = this.moment.add(1, 'months').startOf('month').toDate();
            scope.to = this.moment.add(1, 'months').endOf('month').toDate();
        }

        constructor(protected options: IPresetOptions) {
            super();
        }
    }

    export class NextYearPreset extends Preset {

        static presetStatus = PresetStatus.NextYear;

        getDate(scope: IFromToStructure, option: IPresetOptions = this.options) {
            scope.from = this.moment.add(1, 'years').startOf('years').toDate();
            scope.to = this.moment.add(1, 'years').endOf('years').toDate();
        }

        constructor(protected options: IPresetOptions) {
            super();
        }
    }


    export class LastTwentyFourHoursPreset extends Preset {

        static presetStatus = PresetStatus.LastTwentyFourHours;

        getDate(scope: IFromToStructure, option: IPresetOptions = this.options) {
            scope.from = this.moment.subtract(24, 'hours').startOf('day').toDate();
            scope.to = this.moment.subtract(1, 'days').endOf('day').toDate();
        }

        constructor(protected options: IPresetOptions) {
            super();
        }
    }

    export class LastFourteenDaysPreset extends Preset {

        static presetStatus = PresetStatus.LastFourteenDays;

        getDate(scope: IFromToStructure, option: IPresetOptions = this.options) {
            scope.from = this.moment.subtract(13, 'days').startOf('day').toDate();
            scope.to = this.moment.endOf('day').toDate();
        }

        constructor(protected options: IPresetOptions) {
            super();
        }
    }

    export class LastThirtyDaysPreset extends Preset {

        static presetStatus = PresetStatus.LastThirtyDays;

        getDate(scope: IFromToStructure, option: IPresetOptions = this.options) {
            scope.from = this.moment.subtract(29, 'days').startOf('day').toDate();
            scope.to = this.moment.endOf('day').toDate();
        }

        constructor(protected options: IPresetOptions) {
            super();
        }
    }

    export class LastSixtyDaysPreset extends Preset {

        static presetStatus = PresetStatus.LastSixtyDays;

        getDate(scope: IFromToStructure, option: IPresetOptions = this.options) {
            scope.from = this.moment.subtract(59, 'days').startOf('day').toDate();
            scope.to = this.moment.endOf('day').toDate();
        }

        constructor(protected options: IPresetOptions) {
            super();
        }
    }

    export class LastNinetyDaysPreset extends Preset {

        static presetStatus = PresetStatus.LastNinetyDays;

        getDate(scope: IFromToStructure, option: IPresetOptions = this.options) {
            scope.from = this.moment.subtract(89, 'days').startOf('day').toDate();
            scope.to = this.moment.endOf('day').toDate();
        }

        constructor(protected options: IPresetOptions) {
            super();
        }
    }

    export class LastOneTwentyDaysPreset extends Preset {

        static presetStatus = PresetStatus.LastOneTwentyDays;

        getDate(scope: IFromToStructure, option: IPresetOptions = this.options) {
            scope.from = this.moment.subtract(119, 'days').startOf('day').toDate();
            scope.to = this.moment.endOf('day').toDate();
        }

        constructor(protected options: IPresetOptions) {
            super();
        }
    }

    export class LastOneEightyDaysPreset extends Preset {

        static presetStatus = PresetStatus.LastOneEightyDays;

        getDate(scope: IFromToStructure, option: IPresetOptions = this.options) {
            scope.from = this.moment.subtract(179, 'days').startOf('day').toDate();
            scope.to = this.moment.endOf('day').toDate();
        }

        constructor(protected options: IPresetOptions) {
            super();
        }
    }

    export class SpecificYearPreset extends Preset {

        static presetStatus = PresetStatus.SpecificYear;

        getDate() {
        }

        constructor(protected options: IPresetOptions) {
            super();
        }
    }

}
